/**
 * useTicketStatus - Status and assignment state for an admin report ticket
 *
 * Extracted from useAdminReportData.js. Owns:
 * - Ticket status + status history with optimistic updates
 * - Assignment (assignedTo/At/By) with optimistic updates
 * - Analyst list for the assignment dropdown
 *
 * The parent hook owns isClosed/closedDate (they are also driven by
 * close/reopen outside ticketing), so their setters are injected.
 *
 * @param {string} uuid - The report UUID
 * @param {number} timestamp - The report timestamp (sort key)
 * @param {Object} deps
 * @param {string} deps.organization - Organization for the analyst lookup
 * @param {Function} deps.fetchReport - Refetch the report after a status change
 * @param {Function} deps.clearEscalationState - Reset escalation UI state after actions that restart the clock
 * @param {Function} deps.setIsClosed - Parent setter for closed flag
 * @param {Function} deps.setClosedDate - Parent setter for closed date
 */

import { useState, useCallback } from 'react';

import Util from '../util/util';
import { showSuccess } from '../util/error/hookErrors';
import logger from '../util/logger';
import { t } from '../i18n';

const log = logger.module('TicketStatus');

const useTicketStatus = (
  uuid,
  timestamp,
  { organization, fetchReport, clearEscalationState, setIsClosed, setClosedDate }
) => {
  const [status, setStatus] = useState('Open');
  const [statusHistory, setStatusHistory] = useState([]);
  const [assignedTo, setAssignedTo] = useState(null);
  const [assignedAt, setAssignedAt] = useState(null);
  const [assignedBy, setAssignedBy] = useState(null);
  const [analysts, setAnalysts] = useState([]);
  const [analystsLoading, setAnalystsLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  /** Populate status/assignment state from a fetched report */
  const initializeTicketStatus = useCallback((report) => {
    setStatus(report.status || 'Open');
    setStatusHistory(report.statusHistory || []);
    setAssignedTo(report.assignedTo || null);
    setAssignedAt(report.assignedAt || null);
    setAssignedBy(report.assignedBy || null);
  }, []);

  /**
   * Update report status (optimistic update)
   */
  const updateStatus = useCallback(
    async (newStatus) => {
      // Optimistic update - update UI immediately
      const previousStatus = status;
      setStatus(newStatus);
      setIsClosed(newStatus === 'Closed');
      setStatusUpdating(true);

      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
          status: newStatus,
          changedBy: await Util.getUserEmail(),
        },
      };

      try {
        await Util.patch('Resty', '/items/status', requestData);
        clearEscalationState();
        // Reuses the shared status labels rather than restating them here.
        const statusLabel = t(`reports:status.${newStatus}`, { defaultValue: newStatus });
        showSuccess(t('reports:ticket.statusUpdated', { status: statusLabel }));

        await Util.setReportCache(uuid, 'reportChanged', true);

        // Refresh report to get updated fields (auto-assignment, etc.)
        const updatedReport = await fetchReport();
        if (updatedReport) {
          setStatus(updatedReport.status || newStatus);
          setStatusHistory(updatedReport.statusHistory || []);
          setAssignedTo(updatedReport.assignedTo || null);
          setAssignedAt(updatedReport.assignedAt || null);
          setAssignedBy(updatedReport.assignedBy || null);
          setIsClosed(updatedReport.status === 'Closed');
          setClosedDate(updatedReport.closedDate || null);
        }
      } catch (err) {
        // Revert on failure
        setStatus(previousStatus);
        setIsClosed(previousStatus === 'Closed');
        Util.handleApiError(err, { userMessage: t('reports:ticket.statusUpdateFailed') });
      } finally {
        setStatusUpdating(false);
      }
    },
    [uuid, timestamp, status, fetchReport, clearEscalationState, setIsClosed, setClosedDate]
  );

  /**
   * Update report assignment (optimistic update)
   */
  const updateAssignment = useCallback(
    async (email) => {
      // Optimistic update - update UI immediately
      const previousAssignedTo = assignedTo;
      setAssignedTo(email);

      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
          assignedTo: email || 'null',
        },
      };

      try {
        await Util.patch('Resty', '/items/assign', requestData);
        clearEscalationState();
        showSuccess(
          email ? t('reports:ticket.assignedTo', { email }) : t('reports:ticket.assignmentRemoved'),
        );

        await Util.setReportCache(uuid, 'reportChanged', true);

        // Update additional local state after success
        setAssignedAt(email ? Date.now() : null);
        setAssignedBy(email ? await Util.getUserEmail() : null);
      } catch (err) {
        // Revert on failure
        setAssignedTo(previousAssignedTo);
        Util.handleApiError(err, { userMessage: t('reports:ticket.assignmentUpdateFailed') });
      }
    },
    [uuid, timestamp, assignedTo, clearEscalationState]
  );

  /**
   * Fetch analysts for the organization (for assignment dropdown)
   */
  const fetchAnalysts = useCallback(async () => {
    if (!organization) return [];

    setAnalystsLoading(true);
    try {
      const response = await Util.get('UserManagement', '/users', {
        queryParams: { organization },
      });

      // Handle both response structures: response.data.roles or response.roles
      const rolesArray = response?.data?.roles || response?.roles;

      if (rolesArray && Array.isArray(rolesArray)) {
        // Filter to only analysts from the roles array
        const analystUsers = rolesArray
          .filter((userRole) => userRole.Roles && userRole.Roles.includes('Analyst'))
          .map((userRole) => ({ email: userRole.user }));

        setAnalysts(analystUsers);
        return analystUsers;
      }

      log.warn('No roles array found in analysts response');
      return [];
    } catch (err) {
      log.error(t('reports:ticket.analystsFetchFailed'), { error: err.message });
      return [];
    } finally {
      setAnalystsLoading(false);
    }
  }, [organization]);

  return {
    status,
    statusHistory,
    statusUpdating,
    assignedTo,
    assignedAt,
    assignedBy,
    analysts,
    analystsLoading,
    initializeTicketStatus,
    updateStatus,
    updateAssignment,
    fetchAnalysts,
  };
};

export default useTicketStatus;
